/* eslint-disable max-lines */
import React, { memo, useEffect, useState } from 'react';
import without from 'lodash/without';
import { useTranslate } from '@im/react-localization';
import Button from '@mui/material/Button';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerProvider } from 'src/provider/DatePickerProvider';
import { InputValues, FilterSetting, OperatorType } from './InterfaceTypes';
import { Option } from '@im/ui-lib/SelectOption';

import {
  useSimpleSearchLookUp,
  DealSearchResponse,
  useSimpleSearchProgramLookUp,
} from 'src/hooks/simpleSearchHook';
import { useVendorSearchQuery } from 'src/views/vendorprogram/hooks/apiHook';
import dayjs from 'dayjs';
import Autocomplete, {
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete';

export type OnValueChange = (
  index: number,
  name: string,
  value: InputValues['value'],
  inputType?: OperatorType
) => void;

interface FilterProps {
  businessType: string | null;
  filter: InputValues;
  filterOptions: FilterSetting['filterOptions'];
  filterValues: InputValues[];
  index: number;
  inputType: OperatorType | undefined;
  onAddClick: () => void;
  onDeleteClick: (index: number) => void;
  onValueChange: OnValueChange;
  operators: FilterSetting['operators'];
  setSelectedVendorId: (key: number) => void;
  vendorIdValue: number | null;
}

function Filter({
  businessType,
  filter,
  filterOptions,
  filterValues,
  index,
  inputType,
  onAddClick,
  onDeleteClick,
  onValueChange,
  operators,
  setSelectedVendorId,
  vendorIdValue,
}: Readonly<FilterProps>) {
  const translate = useTranslate();

  const otherFilters = without(filterValues, filter);

  const availableFilterOptions =
    filterOptions.filter(
      (opt) =>
        !otherFilters
          .filter((v) =>
            !v.operator && v.filterOption !== filter.filterOption
              ? true
              : v.operator === filter.operator
          )
          .map((v) => v.filterOption)
          .includes(opt.value)
    ) || [];

  const availableOperators =
    operators.filter(
      (opt) =>
        !otherFilters
          .filter((v) => v.filterOption === filter.filterOption)
          .map((v) => v.operator)
          .includes(opt.value)
    ) || [];

  const filterOption = filterOptions.find(
    (o) => filter.filterOption === o.value
  );

  const enableAddFilter =
    availableOperators.length === 0
      ? true
      : filterValues.length === index + 1 &&
        filterValues.length < filterOptions.length * operators.length;

  const [dealNumberValue, setDealNumberValue] = useState<string>('');
  const [vendorNameValue, setVendorNameValue] = useState<string>('');
  const [programNameValue, setProgramNameValue] = useState<string>('');
  const [autocompleteData, setAutocompleteData] = useState<Option[]>([]);
  const { data: dealNumberData } = useSimpleSearchLookUp({
    businessType: businessType,
    countryCode: 'US',
    dealNameToSearch: dealNumberValue.trim(),
  });
  const { data: vendorNameData } = useVendorSearchQuery({
    businessType: businessType,
    vendorNameToSearch: vendorNameValue.trim(),
  });
  const { data: programNameData } = useSimpleSearchProgramLookUp({
    businessType: businessType,
    programName: programNameValue.trim(),
    vendorIDToSearch: vendorIdValue ?? 0,
  });

  useEffect(() => {
    if (dealNumberValue.trim()) {
      setAutocompleteData(
        dealNumberData ? transformDataToKeyValue(dealNumberData) : []
      );
    } else {
      setAutocompleteData([]);
    }
  }, [dealNumberValue, dealNumberData]);

  useEffect(() => {
    if (vendorNameValue.trim()) {
      setAutocompleteData(
        (vendorNameData as unknown as typeof autocompleteData) ?? []
      );
    } else {
      setAutocompleteData([]);
    }
  }, [vendorNameValue, vendorNameData]);

  useEffect(() => {
    if (programNameValue.trim()) {
      setAutocompleteData(programNameData ?? []);
    } else {
      setAutocompleteData([]);
    }
  }, [programNameValue, programNameData]);
  const onInputChange = (value: string, event?: React.SyntheticEvent) => {
    switch (filter.filterOption) {
      case 'dealNumber':
        setDealNumberValue(value);
        break;
      case 'vendorName':
        setVendorNameValue(value);
        break;
      case 'programName':
        setProgramNameValue(value);
        break;
      default:
        break;
    }

    if (event?.type === 'change' || value === '') {
      if (filter.filterOption === 'vendorName') {
        setSelectedVendorId(0);
      }
      onValueChange(index, 'value', '');
    }
  };
  const transformDataToKeyValue = (data: DealSearchResponse[]): Option[] => {
    return data.map((item: DealSearchResponse) => ({
      label: item.deal_name,
      value: item.importreferenceid.toString(), // Convert 'importreferenceid' to string
    }));
  };

  const options = autocompleteData ?? [];

  const onOptionSelected = (value: string) => {
    if (filter.filterOption === 'vendorName') {
      const key = options.find((option) => option.label === value)?.value;
      setSelectedVendorId(key ? parseInt(key) : 0);
    }
    onValueChange(index, 'value', value);
  };
  const handleOnChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') onOptionSelected(value ?? '');
  };

  return (
    <>
      <Divider
        sx={(theme) => ({
          borderColor: theme.palette.neutral[200],
          marginBottom: theme.spacing(2),
        })}
      />
      <Stack
        data-testid={`gds-vp-advanced-search-settings-widget-item`}
        direction="row"
        spacing={1.5}
      >
        <TextField
          fullWidth
          helperText={translate('Common.FilterOption')}
          name="filterOption"
          onChange={(event) =>
            onValueChange(index, event.target.name, event.target.value)
          }
          placeholder={translate('Common.SelectOption')}
          select
          value={filter.filterOption}
        >
          {availableFilterOptions.map((opt: any) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Typography variant="bodycopyregular">{opt.label}</Typography>
            </MenuItem>
          ))}
        </TextField>

        {!filterOption?.hideOperators &&
          filter.filterOption !== 'businessType' &&
          filter.filterOption !== 'discountLineDetailProductBusinessType' && (
            <TextField
              fullWidth
              helperText={translate('Common.Condition')}
              name="operator"
              onChange={(event) =>
                onValueChange(index, event.target.name, event.target.value)
              }
              select
              value={filter.operator ?? ''}
            >
              {availableOperators.map((opt: any) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Typography variant="bodycopyregular">{opt.label}</Typography>
                </MenuItem>
              ))}
            </TextField>
          )}

        {inputType === 'date' && (
          <DatePickerProvider>
            <DatePicker
              onChange={(value) =>
                onValueChange(index, 'value', dayjs(value).toString())
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={translate('Common.Value')}
                  name="value"
                />
              )}
              value={filter.value || null}
            />
          </DatePickerProvider>
        )}
        {inputType === 'range' && (
          <DatePickerProvider>
            <DatePicker
              onChange={(value) =>
                onValueChange(
                  index,
                  'starts',
                  dayjs(value).toString(),
                  inputType
                )
              }
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={translate('Common.Start')}
                  name="starts"
                />
              )}
              value={filter.value['starts'] ?? null}
            />

            <DatePicker
              minDate={filter.value['starts']}
              onChange={(value) =>
                onValueChange(index, 'ends', dayjs(value).toString(), inputType)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={translate('Common.End')}
                  name="ends"
                  value={filter.value['ends'] ?? ''}
                />
              )}
              value={filter.value['ends'] ?? null}
            />
          </DatePickerProvider>
        )}

        {(inputType === undefined || inputType === 'text') && (
          <TextField
            fullWidth
            helperText={translate('Common.Value')}
            name="value"
            onChange={(event) =>
              onValueChange(index, event.target.name, event.target.value)
            }
            placeholder={translate('Common.SelectOption')}
            select={Boolean(filterOption?.valueOptions)}
            value={filter.value}
          >
            {filterOption?.valueOptions?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Typography variant="bodycopyregular">{opt.label}</Typography>
              </MenuItem>
            ))}
          </TextField>
        )}
        {inputType === 'autocomplete' && (
          <Autocomplete
            clearOnBlur={true}
            data-testid="autocomplete_AdvanceSearch"
            disableClearable
            disabled={
              filter.filterOption === 'programName'
                ? !(vendorIdValue !== 0 && vendorIdValue !== null)
                : false
            }
            freeSolo
            id={`ddlFilter${index}`}
            onChange={handleOnChange}
            onInputChange={(event, newValue) => {
              onInputChange(newValue, event);
            }}
            options={options.map((o) => o.label)}
            renderInput={(params) => (
              <TextField
                data-testid="txt_ddl_AdvanceSearch"
                name="value"
                type="search"
                {...params}
                InputProps={{
                  ...params.InputProps,
                }}
                placeholder={translate('Common.SelectOption')}
                style={{ paddingRight: '0px' }}
                sx={{ padding: '0px' }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-popupIndicator': {
                display: 'none', // Hides the dropdown icon
              },
              '.MuiOutlinedInput-root': {
                borderRadius: '4px',
                paddingRight: '0px',
              },
              '.MuiOutlinedInput-root:has(.MuiInputAdornment-positionStart)': {
                padding: '0px 8px !important',
              },
              fontSize: '14px',
              height: '30px !important',
              width: '100%',
            }}
            value={filter.value}
          />
        )}

        <Stack direction="row" spacing={1}>
          <Button
            aria-label="delete"
            disabled={filterValues.length === 1}
            onClick={() => onDeleteClick(index)}
            sx={{ width: `32px !important` }}
            variant="newOutlined"
          >
            <DeleteOutlineOutlinedIcon />
          </Button>
          <Button
            aria-label={translate('VendorProgram.AdvancedSearchAddFilter')}
            disabled={!enableAddFilter}
            onClick={onAddClick}
            sx={{ width: `32px !important` }}
            variant="newOutlined"
          >
            <AddIcon />
          </Button>
        </Stack>
      </Stack>
    </>
  );
}

export default memo(Filter);
